import "./cookie-consent.ts"; // GDPR banner/preferences
import { tealIce } from "./fx";

const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ============================================================
   NAV — scroll state
   ============================================================ */
const nav = document.getElementById("nav");
if (nav) {
  const onScroll = () => nav.classList.toggle("scrolled", window.scrollY > 24);
  onScroll();
  window.addEventListener("scroll", onScroll, { passive: true });
}

/* ============================================================
   MOBILE MENU
   ============================================================ */
const menuBtn = document.getElementById("menuBtn");
const mobileMenu = document.getElementById("mobileMenu");
if (menuBtn && mobileMenu) {
  menuBtn.addEventListener("click", () => {
    const open = mobileMenu.classList.toggle("open");
    menuBtn.setAttribute("aria-expanded", String(open));
  });
  for (const a of mobileMenu.querySelectorAll("a")) {
    a.addEventListener("click", () => mobileMenu.classList.remove("open"));
  }
}

/* ============================================================
   ACCORDION
   ============================================================ */
const accItems = Array.from(document.querySelectorAll<HTMLElement>(".acc-item"));
const accBody = (item: Element) => item.querySelector<HTMLElement>(".acc-body");
for (const item of accItems) {
  const head = item.querySelector<HTMLElement>(".acc-head");
  const body = accBody(item);
  if (!head || !body) continue;

  if (item.classList.contains("open")) body.style.maxHeight = `${body.scrollHeight}px`;

  head.addEventListener("click", () => {
    const wasOpen = item.classList.contains("open");
    for (const other of accItems) {
      other.classList.remove("open");
      const b = accBody(other);
      if (b) b.style.maxHeight = "0px";
      other.querySelector(".acc-head")?.setAttribute("aria-expanded", "false");
    }
    if (!wasOpen) {
      item.classList.add("open");
      body.style.maxHeight = `${body.scrollHeight}px`;
      head.setAttribute("aria-expanded", "true");
    }
  });
}
window.addEventListener("resize", () => {
  const open = document.querySelector<HTMLElement>(".acc-item.open .acc-body");
  if (open) open.style.maxHeight = `${open.scrollHeight}px`;
});

/* ============================================================
   CARD CURSOR SPOTLIGHT
   ============================================================ */
for (const card of document.querySelectorAll<HTMLElement>(".card")) {
  card.addEventListener("pointermove", (e) => {
    const r = card.getBoundingClientRect();
    card.style.setProperty("--mx", `${e.clientX - r.left}px`);
    card.style.setProperty("--my", `${e.clientY - r.top}px`);
  });
}

/* ============================================================
   CANVAS EFFECTS
   ============================================================ */
interface Surface {
  ctx: CanvasRenderingContext2D;
  w: number;
  h: number;
}
/** Size the canvas backing store to its CSS box at (capped) device pixel ratio. */
function fit(canvas: HTMLCanvasElement): Surface {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  canvas.width = Math.max(1, w * dpr);
  canvas.height = Math.max(1, h * dpr);
  const ctx = canvas.getContext("2d")!;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { ctx, w, h };
}

/** One animated effect bound to a canvas: draw a frame, or recompute on resize. */
interface Effect {
  frame: () => void;
  resize: () => void;
}
type EffectFactory = (canvas: HTMLCanvasElement) => Effect;

/**
 * Drive an effect with requestAnimationFrame, but only while the canvas is
 * actually on screen and the tab is visible. This keeps idle/scrolled-away
 * canvases from burning CPU — the single biggest perf win over the original.
 */
function mount(canvas: HTMLCanvasElement | null, factory: EffectFactory): void {
  if (!canvas) return;
  const effect = factory(canvas);
  window.addEventListener("resize", effect.resize, { passive: true });

  // Reduced-motion: render one static frame and stop.
  if (reduce) {
    effect.frame();
    return;
  }

  let raf = 0;
  let onScreen = false;
  const tick = () => {
    effect.frame();
    raf = requestAnimationFrame(tick);
  };
  const start = () => {
    if (!raf && onScreen && !document.hidden) raf = requestAnimationFrame(tick);
  };
  const stop = () => {
    if (raf) {
      cancelAnimationFrame(raf);
      raf = 0;
    }
  };

  new IntersectionObserver((entries) => {
    onScreen = entries.some((e) => e.isIntersecting);
    if (onScreen) start();
    else stop();
  }).observe(canvas);

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) stop();
    else start();
  });
}

/* ---------- AURORA (hero + horizon) ---------- */
interface AuroraOpts {
  count?: number;
  top?: number;
  spread?: number;
  speed?: number; // multiplier on the drift rate (1 = previous default)
}
function aurora(opts: AuroraOpts = {}): EffectFactory {
  return (canvas) => {
    let { ctx, w, h } = fit(canvas);
    const count = opts.count ?? 18;
    const speedScale = opts.speed ?? 1;
    const strands = Array.from({ length: count }, () => ({
      y: (opts.top ?? 0.1) + Math.random() * (opts.spread ?? 0.55),
      amp: 30 + Math.random() * 130,
      freq: 0.5 + Math.random() * 1.7,
      speed: (0.003 + Math.random() * 0.008) * (Math.random() < 0.5 ? -1 : 1),
      phase: Math.random() * Math.PI * 2,
      hue: Math.random(),
      width: 0.6 + Math.random() * 2.4,
      alpha: 0.05 + Math.random() * 0.14,
    }));
    let t = 0;

    return {
      frame() {
        ctx.clearRect(0, 0, w, h);
        ctx.globalCompositeOperation = "lighter";
        for (const s of strands) {
          ctx.beginPath();
          const steps = 64;
          for (let i = 0; i <= steps; i++) {
            const nx = i / steps;
            const x = nx * w;
            const env = Math.sin(nx * Math.PI); // fade horizontally
            const y =
              s.y * h +
              Math.sin(nx * Math.PI * 2 * s.freq + s.phase + t * s.speed * 60) * s.amp * env +
              Math.sin(nx * Math.PI * 6 + t * s.speed * 30) * s.amp * 0.16 * env;
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          const col = tealIce(s.hue);
          ctx.strokeStyle = `rgba(${col},${s.alpha})`;
          ctx.lineWidth = s.width;
          ctx.shadowColor = `rgba(${col},0.85)`;
          ctx.shadowBlur = 16;
          ctx.stroke();
        }
        t += 0.08 * speedScale;
      },
      resize() {
        ({ ctx, w, h } = fit(canvas));
      },
    };
  };
}

/* ---------- FILAMENTS (Alverion card — pulsing teal) ---------- */
function filaments(): EffectFactory {
  return (canvas) => {
    let { ctx, w, h } = fit(canvas);
    const count = 11;
    const lines = Array.from({ length: count }, () => ({
      y: 0.12 + Math.random() * 0.76,
      amp: 16 + Math.random() * 52,
      freq: 0.7 + Math.random() * 1.6,
      speed: (0.004 + Math.random() * 0.01) * (Math.random() < 0.5 ? -1 : 1),
      phase: Math.random() * 6.28,
      hue: Math.random() * 0.5,
      width: 0.8 + Math.random() * 1.8,
    }));
    let t = 0;

    // Ambient teal bloom — depends only on canvas size, so build it once per
    // resize rather than allocating a new gradient on every frame.
    const makeBloom = () => {
      const g = ctx.createRadialGradient(w * 0.3, h * 0.42, 0, w * 0.3, h * 0.42, Math.max(w, h) * 0.85);
      g.addColorStop(0, "rgba(47,230,196,0.12)");
      g.addColorStop(1, "rgba(47,230,196,0)");
      return g;
    };
    let bloom = makeBloom();

    return {
      frame() {
        ctx.clearRect(0, 0, w, h);
        ctx.globalCompositeOperation = "source-over";
        ctx.fillStyle = bloom;
        ctx.fillRect(0, 0, w, h);

        // Additive blending fakes each filament's glow with two cheap strokes —
        // a soft wide halo plus a bright core. This replaces canvas shadowBlur,
        // which was the scroll-jank culprit: shadowBlur re-renders every stroke
        // through an offscreen blur, and this is the largest card on the page.
        ctx.globalCompositeOperation = "lighter";
        const pulse = 0.5 + 0.5 * Math.sin(t * 0.03);
        for (const s of lines) {
          ctx.beginPath();
          const steps = 40;
          for (let i = 0; i <= steps; i++) {
            const nx = i / steps;
            const x = nx * w;
            const env = Math.sin(nx * Math.PI);
            const y = s.y * h + Math.sin(nx * Math.PI * 2 * s.freq + s.phase + t * s.speed * 60) * s.amp * env;
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          const col = tealIce(s.hue);
          const alpha = 0.16 + pulse * 0.24;
          ctx.strokeStyle = `rgba(${col},${alpha * 0.35})`; // halo
          ctx.lineWidth = s.width * 5;
          ctx.stroke();
          ctx.strokeStyle = `rgba(${col},${alpha})`; // core
          ctx.lineWidth = s.width;
          ctx.stroke();
        }
        t += 0.015; // very slow shimmer (20% of previous)
      },
      resize() {
        ({ ctx, w, h } = fit(canvas));
        bloom = makeBloom();
      },
    };
  };
}

/* ---------- SWARM (Fleet card — coordinated icy specks + links) ---------- */
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  hue: number;
  a: number;
}
function swarm(): EffectFactory {
  return (canvas) => {
    let { ctx, w, h } = fit(canvas);
    const LINK = 78;
    const NEI = 58;
    const SEP = 26;
    const MAXV = 0.62;
    let parts: Particle[] = [];
    let n = 0;

    const seed = () => {
      n = Math.max(40, Math.min(80, Math.floor((w * h) / 5400)));
      parts = Array.from({ length: n }, () => {
        const ang = Math.random() * 6.283;
        const sp = 0.25 + Math.random() * 0.3;
        return {
          x: Math.random() * w,
          y: Math.random() * h,
          vx: Math.cos(ang) * sp,
          vy: Math.sin(ang) * sp,
          r: 2 + Math.random() * 3,
          hue: Math.random() < 0.2 ? Math.random() * 0.3 : 0.82 + Math.random() * 0.18,
          a: 0.5 + Math.random() * 0.45,
        };
      });
    };
    seed();

    return {
      frame() {
        ctx.clearRect(0, 0, w, h);
        // boids: cohesion + alignment + separation
        for (let i = 0; i < n; i++) {
          const p = parts[i];
          let cx = 0,
            cy = 0,
            ax = 0,
            ay = 0,
            sx = 0,
            sy = 0,
            neighbours = 0;
          for (let j = 0; j < n; j++) {
            if (i === j) continue;
            const dx = parts[j].x - p.x;
            const dy = parts[j].y - p.y;
            const d2 = dx * dx + dy * dy;
            if (d2 < NEI * NEI) {
              neighbours++;
              cx += parts[j].x;
              cy += parts[j].y;
              ax += parts[j].vx;
              ay += parts[j].vy;
              if (d2 < SEP * SEP) {
                const d = Math.sqrt(d2) || 1;
                sx -= dx / d;
                sy -= dy / d;
              }
            }
          }
          if (neighbours > 0) {
            p.vx += (cx / neighbours - p.x) * 0.0011 + (ax / neighbours) * 0.045 + sx * 0.05;
            p.vy += (cy / neighbours - p.y) * 0.0011 + (ay / neighbours) * 0.045 + sy * 0.05;
          }
          p.vx += (Math.random() - 0.5) * 0.018;
          p.vy += (Math.random() - 0.5) * 0.018;
          const v = Math.hypot(p.vx, p.vy);
          if (v > MAXV) {
            p.vx = (p.vx / v) * MAXV;
            p.vy = (p.vy / v) * MAXV;
          }
          p.x += p.vx;
          p.y += p.vy;
          if (p.x > w + 6) p.x = -6;
          if (p.x < -6) p.x = w + 6;
          if (p.y > h + 6) p.y = -6;
          if (p.y < -6) p.y = h + 6;
        }

        ctx.globalCompositeOperation = "lighter";
        // links between nearby agents — reads as a coordinated fleet
        ctx.lineWidth = 0.7;
        for (let i = 0; i < n; i++) {
          for (let j = i + 1; j < n; j++) {
            const dx = parts[i].x - parts[j].x;
            const dy = parts[i].y - parts[j].y;
            const d2 = dx * dx + dy * dy;
            if (d2 < LINK * LINK) {
              ctx.strokeStyle = `rgba(108,184,255,${(1 - Math.sqrt(d2) / LINK) * 0.18})`;
              ctx.beginPath();
              ctx.moveTo(parts[i].x, parts[i].y);
              ctx.lineTo(parts[j].x, parts[j].y);
              ctx.stroke();
            }
          }
        }
        // specks — a faint halo disc + a bright core under additive blending,
        // replacing the per-particle shadowBlur (same fix as the filaments card).
        for (let k = 0; k < n; k++) {
          const q = parts[k];
          const col = tealIce(q.hue);
          ctx.fillStyle = `rgba(${col},${q.a * 0.18})`; // halo
          ctx.beginPath();
          ctx.arc(q.x, q.y, q.r * 3, 0, 6.283);
          ctx.fill();
          ctx.fillStyle = `rgba(${col},${q.a})`; // core
          ctx.beginPath();
          ctx.arc(q.x, q.y, q.r, 0, 6.283);
          ctx.fill();
        }
      },
      resize() {
        ({ ctx, w, h } = fit(canvas));
        seed();
      },
    };
  };
}

/* ============================================================
   BOOT
   ============================================================ */
function boot(): void {
  mount(document.getElementById("aurora") as HTMLCanvasElement | null, aurora({ count: 20, top: 0.06, spread: 0.55, speed: 0.2 }));
  mount(document.getElementById("aurora2") as HTMLCanvasElement | null, aurora({ count: 12, top: 0.35, spread: 0.5 }));
  for (const c of document.querySelectorAll<HTMLCanvasElement>('[data-fx="filaments"]')) mount(c, filaments());
  for (const c of document.querySelectorAll<HTMLCanvasElement>('[data-fx="swarm"]')) mount(c, swarm());
}

if (document.readyState !== "loading") boot();
else document.addEventListener("DOMContentLoaded", boot);
